import { isEmpty } from "lodash";
import { type Options, serialize } from "object-to-formdata";

import type {
  OrganizationId,
  Permission,
  RoleId,
} from "@/apps/admin/api/authTypes";

import { isRecord } from "./typeGuard";

// A permission the signed-in user holds, with its `objectType.name` token and the organization
// it is scoped to (null: global).
export type DenormalizedPermission = Permission & {
  organizationId: OrganizationId | null;
  permission: string;
};

// A role assignment: an organization role (roleId) or a management role (managementRoleId).
type RoleGrant = {
  roleId?: RoleId;
  managementRoleId?: RoleId;
  organizationId?: OrganizationId | null;
};

type RoleWithPermissions = {
  id: RoleId;
  permissions?: Permission[];
};

// Permissions come from the store or from callers that hold them as `unknown`; anything but a
// list of `{ permission }` entries grants nothing.
const holdsPermission = (
  permissions: unknown,
  matches: (entry: Record<string, unknown>) => boolean
): boolean => {
  if (!Array.isArray(permissions) || isEmpty(permissions)) {
    return false;
  }
  return permissions.some((entry) => isRecord(entry) && matches(entry));
};

export const hasPermissionToken = (
  permissions: unknown,
  objectType: string,
  name: string
): boolean =>
  holdsPermission(
    permissions,
    ({ permission }) => permission === `${objectType}.${name}`
  );

export const checkGlobalManagementRolePermission = (
  permissions: unknown,
  objectType: string,
  name: string
): boolean =>
  holdsPermission(
    permissions,
    ({ permission, organizationId }) =>
      !organizationId && permission === `${objectType}.${name}`
  );

/**
 * This function flatten user's roles into permission
 * and append permission token to every permission object.
 * @returns Array of permissions.
 */
export const denormalizePermissions = (
  userRoles: readonly RoleGrant[],
  roles: readonly RoleWithPermissions[]
): DenormalizedPermission[] => {
  if (isEmpty(userRoles) || isEmpty(roles)) {
    return [];
  }
  return userRoles.flatMap((userRole) => {
    const role = roles.find(
      (candidate) =>
        candidate.id === (userRole.roleId || userRole.managementRoleId)
    );
    // An org-scoped assignment only grants its permissions inside that organization
    return (role?.permissions ?? []).map((permission) => ({
      ...permission,
      organizationId: userRole.organizationId ?? null,
      permission: `${permission.objectType}.${permission.name}`,
    }));
  });
};

/**
 * Processes form data by converting empty arrays to JSON strings and serializes to FormData.
 * This ensures empty arrays are properly sent to the backend via FormData.
 * @param data - The data object to process
 * @param serializeOptions - Options for the serialize function (optional)
 * @returns Serialized FormData with empty arrays converted to "[]" strings
 */
export const transformToFormData = (
  data: Record<string, unknown>,
  serializeOptions: Options = { indices: true, nullsAsUndefineds: true }
): FormData => {
  const processedData: Record<string, unknown> = { ...data };

  // Convert all empty arrays to "[]" strings so they get included in FormData
  Object.keys(processedData).forEach((field) => {
    const value = processedData[field];
    if (Array.isArray(value) && value.length === 0) {
      processedData[field] = "[]";
    }
  });

  // Serialize the processed data to FormData
  return serialize(processedData, serializeOptions);
};
